"use strict";

import { from, of } from "rxjs";
import { ajax } from "rxjs/ajax";
import { ofType } from "redux-observable";
import { mergeMap, catchError, map, concatMap } from "rxjs/operators";
import { settingConstants, responseCodes } from "../_constants";
import { getRequestHeader } from "../../_helpers/request.header";
import {
  ajaxError,
  settingChangePasscodeComplete,
  settingChangeOperatingTypeComplete,
  settingGetAllAccountsComplete,
  settingGetSelectedAccountsComplete,
  settingAuthenticateComplete,
  settingAuthenticateError,
  settingGetAllNotificationCategoryComplete,
  settingGetAllNotificationCategoryRequest,
  settingUpdateNotificationCategoryComplete,
  settingChangePasswordComplete,
  settingSetAuthForm,
  settingGetAllSavingAccountsComplete,
  updateDefaultAccountComplete,
  payloadEncryptionSuccess,
  payloadEncryptionFail,
  setAccountNicknameComplete,
  setAccountNicknameError,
  settingGetTransferLimitsComplete,
  settingAddTransferLimitComplete,
  settingChangePasscodeValidateComplete,
  settingChangePasscodeOtpResendComplete,
  settingChangePasscodeOtpConfirmComplete,
  settingChangePasscodeOtpConfirmError,
  settingOtpPrefGetTwofaMethodComplete,
  settingOtpPrefGetTwofaMethodError,
  settingOtpPrefGetTwofaRequestOtpComplete,
  settingOtpPrefGetTwofaRequestOtpError,
  settingOtpPrefGetTwofaResendOtpComplete,
  settingOtpPrefGetTwofaResendOtpError,
  settingOtpPrefGetTwofaConfirmOtpComplete,
  settingOtpPrefGetTwofaConfirmOtpError,
  settingGetTransferLimitsError,
  settingAddTransferLimitError,
  settingGetAllSavingAccountsError,
  settingGetAllCreditCardPrefComplete,
  settingGetAllCreditCardPrefError,
  settingChangeCreditCardOperatingTypeComplete,
  settingChangeCreditCardOperatingTypeError,
  settingGetAllAccountsError,
  settingChangeOperatingTypeError,
  updateDefaultAccountError,
  settingChangePasscodeError,
  settingGetNotificationCategoriesComplete,
  settingGetNotificationCategoriesError,
  updateNotificationCategoryComplete,
  updateNotificationCategoryError,
  settingChangePasscodeOtpResendError,
} from "../_actions";
import { payloadFormatter } from "../../_helpers/encryption.helper";

export const settingChangePasswordEpic = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.SETTING_CHANGE_PASSWORD_REQUEST),
    mergeMap(async (action) => {
      try {
        const payload = await from(
          payloadFormatter(action.payload, "/api/setting/change-password"),
        ).toPromise();
        return payloadEncryptionSuccess(payload, action.payload);
      } catch (error) {
        return payloadEncryptionFail(error);
      }
    }),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/setting/change-password",
          action.encryptedPayload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            settingChangePasswordComplete(ajaxResponse.response),
          ),
          catchError((error) => of(ajaxError(error))),
        );
    }),
  );

export const settingChangePasscodeEpic = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.SETTING_CHANGE_PASSCODE_REQUEST),
    mergeMap(async (action) => {
      const { setShowOtpModal, ...cleanPayload } = action.payload || {};
      try {
        const payload = await from(
          payloadFormatter(cleanPayload, "/api/setting/change-passcode"),
        ).toPromise();
        // Attach setShowOtpModal so it is available in the next mergeMap
        return {
          ...payloadEncryptionSuccess(payload, cleanPayload),
          setShowOtpModal,
        };
      } catch (error) {
        return payloadEncryptionFail(error);
      }
    }),
    mergeMap((action) => {
      const { setShowOtpModal } = action;
      return ajax
        .post(
          "/api/setting/change-passcode",
          action.encryptedPayload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) => {
            // Open OTP modal immediately after successful change-passcode init
            if (typeof setShowOtpModal === "function") {
              setShowOtpModal(true);
            }
            return settingChangePasscodeComplete(ajaxResponse.response);
          }),
          catchError((error) =>
            of(settingChangePasscodeError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const settingChangePasscodeOTPResendEpic = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.SETTING_CHANGE_PASSCODE_OTP_RESEND_REQUEST),
    mergeMap(async (action) => {
      try {
        const payload = await from(
          payloadFormatter(
            action.payload,
            "/api/setting/change-passcode-otp-resend",
          ),
        ).toPromise();
        return payloadEncryptionSuccess(payload, action.payload);
      } catch (error) {
        return payloadEncryptionFail(error);
      }
    }),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/setting/change-passcode-otp-resend",
          action.encryptedPayload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            settingChangePasscodeOtpResendComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(settingChangePasscodeOtpResendError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const settingChangePasscodeOTPConfirmEpic = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.SETTING_CHANGE_PASSCODE_OTP_CONFIRM_REQUEST),
    mergeMap(async (action) => {
      try {
        const payload = await from(
          payloadFormatter(
            action.payload,
            "/api/setting/change-passcode-otp-confirm",
          ),
        ).toPromise();
        return payloadEncryptionSuccess(payload, action.payload);
      } catch (error) {
        return payloadEncryptionFail(error);
      }
    }),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/setting/change-passcode-otp-confirm",
          action.encryptedPayload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            settingChangePasscodeOtpConfirmComplete(ajaxResponse.response),
          ),
          catchError((error) => {
            {
              if (
                error?.response?.statusCode &&
                error?.response?.statusCode != responseCodes.SYSTEM_ERROR
              ) {
                return of(ajaxError(error));
              } else {
                return of(
                  settingChangePasscodeOtpConfirmError(action?.payload),
                );
              }
            }
          }),
        );
    }),
  );

export const settingAuthenticateEpic = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.SETTING_AUTHENTICATE_REQUEST),
    mergeMap(async (action) => {
      try {
        const { navigate, redirectUrl, ...cleanPayload } = action.payload;
        const payload = await from(
          payloadFormatter(cleanPayload, "/api/setting/authenticate"),
        ).toPromise();
        return payloadEncryptionSuccess(payload, action.payload);
      } catch (error) {
        return payloadEncryptionFail(error);
      }
    }),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/setting/authenticate",
          action.encryptedPayload,
          getRequestHeader("POST"),
        )
        .pipe(
          mergeMap((ajaxResponse) => {
            const actions = [
              settingAuthenticateComplete(ajaxResponse.response),
            ];
            if (action?.payload?.navigate && action?.payload?.redirectUrl) {
              action.payload.navigate(action.payload.redirectUrl);
            }

            return of(...actions);
          }),
          catchError((error) => {
            {
              if (
                responseCodes.SETTING_AUTH_ERROR_PASSWORD_INCORRECT ==
                error.response.statusCode
              ) {
                return of(ajaxError(error), settingSetAuthForm(true));
              } else {
                return of(settingAuthenticateError(), ajaxError(error));
              }
            }
          }),
        );
    }),
  );

export const settingChangePasscodeValidateEpic = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.SETTING_CHANGE_PASSCODE_VALIDATE_REQUEST),
    mergeMap(async (action) => {
      try {
        const payload = await from(
          payloadFormatter(
            action.payload,
            "/api/setting/change-passcode-validate",
          ),
        ).toPromise();
        return payloadEncryptionSuccess(payload, action.payload);
      } catch (error) {
        return payloadEncryptionFail(error);
      }
    }),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/setting/change-passcode-validate",
          action.encryptedPayload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            settingChangePasscodeValidateComplete(ajaxResponse.response),
          ),
          catchError((error) => of(ajaxError(error))),
        );
    }),
  );

export const getAllAccountsDataEpic = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.SETTING_GET_ALL_ACCOUNTS_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/setting/getAllAccountsData",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            settingGetAllAccountsComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(settingGetAllAccountsError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const getSelectedAccountData = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.SETTING_GET_SELECTED_ACCOUNTS_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/setting/getSelectedAccountData",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            settingGetSelectedAccountsComplete(ajaxResponse.response),
          ),
          catchError((error) => of(ajaxError(error))),
        );
    }),
  );

export const changeOperatingType = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.SETTING_CHANGE_OPERATING_TYPE_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/setting/changeOperatingType",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            settingChangeOperatingTypeComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(settingChangeOperatingTypeError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const getNotificationCategoriesDataEpic = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.SETTING_GET_NOTIFICATION_CATEGORIES_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/setting/getNotificationCategoriesData",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            settingGetNotificationCategoriesComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(settingGetNotificationCategoriesError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const updateNotificationCategoryEpic = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.UPDATE_NOTIFICATION_CATEGORY_REQUEST),

    mergeMap((action) => {
      return ajax
        .post(
          "/api/setting/updateNotificationCategory",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            updateNotificationCategoryComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(updateNotificationCategoryError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const getAllSavingAccountsDataEpic = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.SETTING_GET_ALL_SAVING_ACCOUNTS_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/setting/getAllSavingAccountsData",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            settingGetAllSavingAccountsComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(settingGetAllSavingAccountsError(error), ajaxError(error)),
          ),
        );
    }),
  );
export const updateDefaultAccountEpic = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.UPDATE_DEFAULT_ACCOUNT_REQUEST),

    mergeMap((action) => {
      return ajax
        .post(
          "/api/setting/updateDefaultAccount",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            updateDefaultAccountComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(updateDefaultAccountError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const setAccountNickNameEpic = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.SET_ACCOUNT_NICKNAME_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/setting/setAccountNickName",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            setAccountNicknameComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(ajaxError(error), setAccountNicknameError(error)),
          ),
        );
    }),
  );

export const settingOtpPrefGetTwofaMethodEpic = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.SETTING_OTP_PREF_GET_TWOFA_METHOD_REQUEST),
    mergeMap((action) => {
      const { navigate, redirectUrl, ...cleanPayload } = action?.payload || {};

      return ajax
        .post(
          "/api/setting/get-twofa-method",
          cleanPayload,
          getRequestHeader("POST"),
        )
        .pipe(
          mergeMap((ajaxResponse) => {
            const actions = [
              settingOtpPrefGetTwofaMethodComplete(ajaxResponse.response),
            ];
            if (navigate && redirectUrl) {
              navigate(redirectUrl);
            }

            return of(...actions);
          }),
          catchError((error) =>
            of(ajaxError(error), settingOtpPrefGetTwofaMethodError(error)),
          ),
        );
    }),
  );

export const settingOtpPrefGetTwofaRequestOtpEpic = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.SETTING_OTP_PREF_GET_TWOFA_SEND_OTP_REQUEST),
    mergeMap((action) => {
      const { setShowOtpModal, ...cleanPayload } = action.payload;
      return ajax
        .post(
          "/api/setting/twofa-method-request-otp",
          cleanPayload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) => {
            setShowOtpModal(true);

            return settingOtpPrefGetTwofaRequestOtpComplete(
              ajaxResponse.response,
            );
          }),

          catchError((error) => {
            if (
              error.response?.errorResponse?.responseCode ==
                responseCodes.OTP_FAILED ||
              error.response?.errorResponse?.responseCode ==
                responseCodes.OTP_EXPIRED ||
              error.response?.errorResponse?.responseCode ==
                responseCodes.OTP_BLOCKED ||
              error.response?.errorResponse?.responseCode ==
                responseCodes.OTP_REQUEST_LIMIT_REACHED
            ) {
              setShowOtpModal(true);
            }
            return of(
              ajaxError(error),
              settingOtpPrefGetTwofaRequestOtpError(error),
            );
          }),
        );
    }),
  );

export const settingOtpPrefGetTwofaResendOtpEpic = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.SETTING_OTP_PREF_GET_TWOFA_RESEND_OTP_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/setting/twofa-method-resend-otp",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            settingOtpPrefGetTwofaResendOtpComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(ajaxError(error), settingOtpPrefGetTwofaResendOtpError(error)),
          ),
        );
    }),
  );

export const settingOtpPrefGetTwofaConfirmOtpEpic = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.SETTING_OTP_PREF_GET_TWOFA_CONFORM_OTP_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/setting/twofa-method-confirm-otp",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            settingOtpPrefGetTwofaConfirmOtpComplete(ajaxResponse.response),
          ),
          catchError((error) => {
            {
              if (
                error?.response?.statusCode &&
                error?.response?.statusCode != responseCodes.SYSTEM_ERROR
              ) {
                return of(ajaxError(error));
              } else {
                return of(settingOtpPrefGetTwofaConfirmOtpError(error));
              }
            }
          }),
        );
    }),
  );
export const getTransferLimitEpic = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.SETTING_GET_TRANSFER_LIMITS_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/setting/getTransferLimits",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            settingGetTransferLimitsComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(ajaxError(error), settingGetTransferLimitsError(error)),
          ),
        );
    }),
  );

export const addTransferLimitEpic = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.SETTING_ADD_TRANSFER_LIMIT_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/setting/addTransferLimit",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            settingAddTransferLimitComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(ajaxError(error), settingAddTransferLimitError(error)),
          ),
        );
    }),
  );

export const getAllCreditCardPreferenceEpic = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.SETTING_GET_ALL_CREDIT_CARD_PREF_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/setting/getAllCreditCardPreference",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            settingGetAllCreditCardPrefComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(ajaxError(error), settingGetAllCreditCardPrefError(error)),
          ),
        );
    }),
  );

export const setCreditCardPreferenceEpic = (actions$) =>
  actions$.pipe(
    ofType(settingConstants.SETTING_CHANGE_CREDIT_CARD_OPERATING_TYPE_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/setting/changeCreditCardOperatingType",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            settingChangeCreditCardOperatingTypeComplete(ajaxResponse.response),
          ),
          catchError((error) => {
            {
              if (
                error?.response?.statusCode &&
                error?.response?.statusCode != responseCodes.SYSTEM_ERROR
              ) {
                return of(ajaxError(error));
              } else {
                return of(settingChangeCreditCardOperatingTypeError(error));
              }
            }
          }),
        );
    }),
  );
